// client/src/components/TransactionsTab.jsx
// "עסקאות" tab on CampDetailPage. Read-only history.
//
// Each row shows: timestamp · student · device + mode · direction badge ·
// amount · itemLabel. SPEND rows are expandable to reveal the cart line
// items.

import { useEffect, useState } from 'react';
import { useTransactions } from '../hooks/useTransactions.js';
import Spinner from './Spinner.jsx';
import ListToolbar from './ListToolbar.jsx';
import { formatDateTime, formatMoney } from '../lib/format.js';
import { ArrowsRightLeftIcon } from './Icons.jsx';

const PAGE_SIZE = 50;

// Server-side sort keys (the list is paginated, so sorting must happen there).
const SORT_OPTIONS = [
  { key: 'createdAt', label: 'תאריך' },
  { key: 'amount', label: 'סכום' },
];

export default function TransactionsTab({ campId, currency }) {
  const [filters, setFilters] = useState({ direction: '' });
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [page, setPage] = useState(0);
  // Free text is debounced so each keystroke doesn't hit the server.
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const query = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    sort: sort.key,
    dir: sort.dir,
    ...(filters.direction ? { direction: filters.direction } : {}),
    ...(q ? { q } : {}),
  };
  const txQ = useTransactions(campId, query);
  const list = txQ.data;
  const total = list?.total ?? 0;
  const hasMore = total > (page + 1) * PAGE_SIZE;

  function setDirection(direction) {
    setFilters({ direction });
    setPage(0);
  }

  return (
    <div className="space-y-4">
      <ListToolbar
        search={{ value: qInput, onChange: setQInput, placeholder: 'חיפוש תלמיד או פריט...' }}
        sort={{ value: sort, onChange: (next) => { setSort(next); setPage(0); }, options: SORT_OPTIONS }}
        count={list ? { total, noun: 'עסקאות' } : undefined}
      >
        <div className="flex gap-1.5">
          <DirChip active={filters.direction === ''}       label="הכל"   onClick={() => setDirection('')} />
          <DirChip active={filters.direction === 'CREDIT'} label="זיכויים" onClick={() => setDirection('CREDIT')} tone="mint" />
          <DirChip active={filters.direction === 'DEBIT'}  label="חיובים"  onClick={() => setDirection('DEBIT')}  tone="sky" />
        </div>
      </ListToolbar>

      {txQ.isLoading && !list ? (
        <Spinner label="טוען עסקאות..." />
      ) : txQ.isError ? (
        <div className="card-danger p-4 text-joy-900 text-sm">שגיאה: {txQ.error.message}</div>
      ) : list.transactions.length === 0 ? (
        q || filters.direction ? (
          <div className="card p-8 text-center text-sm text-ink-500">לא נמצאו עסקאות מתאימות לסינון.</div>
        ) : <EmptyState />
      ) : (
        <>
          <ul className="card divide-y divide-ink-100 overflow-hidden">
            {list.transactions.map((t) => (
              <TxRow key={t.id} t={t} currency={currency} />
            ))}
          </ul>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            hasMore={hasMore}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}
    </div>
  );
}

function DirChip({ active, label, onClick, tone }) {
  // Use a tone-specific active style when one is supplied; "all" stays brand.
  let cls = active ? 'chip-active' : 'chip';
  if (active && tone === 'mint') cls = 'chip bg-mint-600 border-mint-600 text-white hover:bg-mint-700 shadow-sm';
  if (active && tone === 'sky')  cls = 'chip bg-sky-600  border-sky-600  text-white hover:bg-sky-700  shadow-sm';
  return (
    <button type="button" onClick={onClick} className={cls}>
      {label}
    </button>
  );
}

function TxRow({ t, currency }) {
  const [open, setOpen] = useState(false);
  const isCredit = t.direction === 'CREDIT';
  const hasLines = t.lines && t.lines.length > 0;
  return (
    <li className={`transition-colors ${hasLines ? 'cursor-pointer' : ''}`} onClick={() => hasLines && setOpen(!open)}>
      <div className="px-4 py-3 hover:bg-ink-50/60 flex items-center gap-3">
        {/* Direction stripe — a small coloured pill on the leading edge so the
            row's tone is obvious before any reading. */}
        <div
          className={`w-1.5 self-stretch rounded-full ${isCredit ? 'bg-mint-500' : 'bg-sky-500'}`}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink-900">
              {t.student
                ? `${t.student.firstName} ${t.student.lastName}`
                : <span className="text-ink-400">—</span>}
            </span>
            {t.student?.className && <span className="badge-slate">{t.student.className}</span>}
            <span className={isCredit ? 'badge-mint' : 'badge-sky'}>
              {isCredit ? 'זיכוי' : 'חיוב'}
            </span>
          </div>
          <div className="text-xs text-ink-500 mt-0.5 flex flex-wrap items-center gap-x-2">
            <span className="num">{formatDateTime(t.createdAt)}</span>
            <span aria-hidden>·</span>
            {/* Device-less transactions are EITHER a voucher redemption (has a
                voucher relation → "מימוש שובר") OR a manual/back-office credit
                — single, bulk or Excel load → "ידני". Only key off the voucher
                relation, not deviceId, or every manual load reads as a voucher. */}
            <span>{t.device?.name || (t.voucher ? 'מימוש שובר' : t.deviceId == null ? 'ידני' : '—')}</span>
            <span aria-hidden>·</span>
            <span>{t.itemLabel}</span>
            {hasLines && (
              <span className="text-brand-600 font-semibold">
                {open ? '▾ סגור פירוט' : '▸ הצג פירוט'}
              </span>
            )}
          </div>
        </div>
        <div className={`font-bold num whitespace-nowrap text-lg ${isCredit ? 'text-mint-700' : 'text-sky-700'}`}>
          {isCredit ? '+' : '−'}{formatMoney(t.amount, currency)}
        </div>
      </div>

      {open && hasLines && (
        <div className="bg-ink-50/70 border-t border-ink-100 px-4 py-3 animate-fade-in">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500 mb-2">סל מכירה</div>
          <ul className="space-y-1.5">
            {t.lines.map((l) => (
              <li key={l.id} className="flex justify-between gap-4 text-sm">
                <span className="text-ink-700">
                  {l.itemLabel}
                  {l.quantity > 1 && <span className="text-ink-400"> · ×{l.quantity}</span>}
                </span>
                <span className="text-ink-900 font-semibold num">
                  {formatMoney(l.amount * l.quantity, currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function Pagination({ page, pageSize, total, hasMore, onPrev, onNext }) {
  if (total <= pageSize && page === 0) return null;
  return (
    <div className="flex items-center justify-between text-sm text-ink-600">
      <div className="num">
        מציג {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} מתוך {total}
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn-secondary btn-sm" onClick={onPrev} disabled={page === 0}>קודם</button>
        <button type="button" className="btn-secondary btn-sm" onClick={onNext} disabled={!hasMore}>הבא</button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-sky-gradient text-white grid place-items-center mb-4 shadow-pop">
        <ArrowsRightLeftIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">אין עדיין עסקאות</h2>
      <p className="mt-2 text-muted max-w-md mx-auto">
        עסקאות יופיעו כאן ברגע שמורים מתחילים לזכות תלמידים או שעמדות
        מכירה מתחילות לחייב.
      </p>
    </div>
  );
}
